import { useEffect, useState } from 'react';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  traffic_source?: string;
}

export function useUTMTracking() {
  const [utmParams, setUtmParams] = useState<UTMParams>({});
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // If already dismissed this session, never show again
    if (sessionStorage.getItem('utm_banner_dismissed') === 'true') {
      // Still load params for tracking purposes, just don't show banner
      const stored = sessionStorage.getItem('utm_params');
      if (stored) {
        try { setUtmParams(JSON.parse(stored)); } catch { /* ignore */ }
      }
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer;

    const utmData: UTMParams = {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      referrer: referrer || undefined,
    };

    // Determine traffic source
    if (utmData.utm_source) {
      utmData.traffic_source = utmData.utm_source;
    } else if (referrer) {
      if (referrer.includes('google.com')) {
        utmData.traffic_source = 'Organic Search - Google';
      } else if (referrer.includes('facebook.com') || referrer.includes('fb.com')) {
        utmData.traffic_source = 'Facebook';
      } else if (referrer.includes('instagram.com')) {
        utmData.traffic_source = 'Instagram';
      } else if (referrer.includes('linkedin.com')) {
        utmData.traffic_source = 'LinkedIn';
      } else if (referrer.includes('bing.com')) {
        utmData.traffic_source = 'Organic Search - Bing';
      } else if (referrer.includes('yahoo.com')) {
        utmData.traffic_source = 'Organic Search - Yahoo';
      } else {
        utmData.traffic_source = 'Referral Traffic';
      }
    } else {
      utmData.traffic_source = 'Direct Traffic';
    }

    setUtmParams(utmData);

    // Show banner if there are UTM params or an external referrer
    if (utmData.utm_source || (referrer && !referrer.includes(window.location.hostname))) {
      setShowBanner(true);
      sessionStorage.setItem('utm_params', JSON.stringify(utmData));
    } else {
      // Try to load from sessionStorage (persisted from a previous page in this session)
      const stored = sessionStorage.getItem('utm_params');
      if (stored) {
        try {
          const storedData = JSON.parse(stored);
          setUtmParams(storedData);
          setShowBanner(true);
        } catch { /* ignore malformed data */ }
      }
    }
  }, []);

  const dismissBanner = () => {
    setShowBanner(false);
    // Persist dismissal so banner stays hidden across page navigations
    sessionStorage.setItem('utm_banner_dismissed', 'true');
  };

  return { utmParams, showBanner, dismissBanner };
}
